import { useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { t } from '../i18n/index.js';
import { Logo } from '../components/Logo.js';
import { Footer } from '../components/Footer.js';
import { NewPasswordFields, newPasswordError } from '../components/PasswordFields.js';
import type { MeUser } from '../../shared/protocol.js';

/**
 * A new user or one whose password the administrator has reset must choose an own password
 * before entering the arena (decision 2026-09-27). The temporary password is asked again.
 */
export function PasswordChangeView(props: { user: MeUser; onDone: () => void; onLogout: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: Event) => {
    e.preventDefault();
    const pwError = newPasswordError(next, repeat, t.account.mismatch);
    if (pwError) return setError(pwError);
    if (next === current) return setError(t.errors.same_password);
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/api/auth/password', { currentPassword: current, newPassword: next });
      props.onDone();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'network';
      setError(code === 'invalid_credentials' ? t.account.currentWrong : t.errors[code]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="center page-with-footer">
      <form class="card login" onSubmit={submit}>
        <h1 class="login-logo">
          <Logo size="large" />
        </h1>
        <h2 class="center-text">{t.passwordChange.title}</h2>
        <p class="subtitle">{t.passwordChange.intro}</p>
        <input type="text" autocomplete="username" value={props.user.username} hidden readOnly />
        <label>
          {t.passwordChange.current}
          <input type="password" value={current} onInput={(e) => setCurrent(e.currentTarget.value)} autocomplete="current-password" required autofocus />
        </label>
        <NewPasswordFields
          value={next}
          repeat={repeat}
          onValue={setNext}
          onRepeat={setRepeat}
          label={t.account.newPassword}
          repeatLabel={t.account.repeatPassword}
        />
        {error && (
          <p class="error" role="alert">
            {error}
          </p>
        )}
        <button class="btn btn-primary" type="submit" disabled={busy}>
          {t.passwordChange.submit}
        </button>
        <button class="btn btn-ghost" type="button" onClick={props.onLogout}>
          {t.nav.logout}
        </button>
      </form>
      <Footer />
    </div>
  );
}
